'use client';

import Image from 'next/image';
import type { Broker } from './types';
import type { ToolsTranslations } from '@/lib/translations/types';

interface Props {
  broker: Broker;
  translations: ToolsTranslations['brokerComparison'];
  /**
   * Optional override for the secondary "review" CTA on the right side of
   * the action row. When provided, replaces both the broker's review URL
   * and label — used by the SingleBroker view to point readers to a
   * comparison/alternatives page instead of the broker's own review.
   */
  reviewCtaOverride?: { label: string; url: string };
}

const STAR_SVG = (filled: boolean) => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill={filled ? '#f59e0b' : 'none'}
    stroke={filled ? '#f59e0b' : '#e5e7eb'}
    strokeWidth={filled ? 0 : 1.5}
    aria-hidden="true"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

export default function BrokerCard({
  broker,
  translations,
  reviewCtaOverride,
}: Props) {
  const t = translations;

  const ACCENT = '#1d4ed8';

  // Pick the secondary CTA: an explicit override wins, otherwise fall
  // back to the broker's own review URL/label (existing behavior).
  const reviewCta = reviewCtaOverride
    ? reviewCtaOverride
    : broker.reviewCtaUrl
      ? { url: broker.reviewCtaUrl, label: broker.reviewCtaLabel || t.readReview }
      : null;

  // A metric is "empty" when the broker simply doesn't have data for it.
  // We treat the placeholder dash and whitespace as empty so blank cards
  // don't render half-filled boxes.
  const isEmpty = (s: string | null | undefined) => {
    if (!s) return true;
    const trimmed = s.trim();
    return trimmed === '' || trimmed === '—' || trimmed === '-';
  };

  type MetricItem = { label: string; value: string; note?: string };
  const metrics: MetricItem[] = [];

  // Fee takes priority over courtage. Fee is the broader platform/management
  // fee (relevant for fund robots, all-in-one brokers); courtage is the per-
  // trade commission. Brokers usually want to highlight whichever is the
  // headline number — and only one is meaningful at a time on the card.
  if (!isEmpty(broker.feeValue)) {
    metrics.push({
      label: t.metrics.fee,
      value: broker.feeValue,
      note: broker.feeDescription,
    });
  } else if (!isEmpty(broker.courtageValue)) {
    metrics.push({
      label: t.metrics.courtage,
      value: broker.courtageValue,
      note: broker.courtageDescription,
    });
  }
  if (!isEmpty(broker.minDepositValue)) {
    metrics.push({
      label: t.metrics.minDeposit,
      value: broker.minDepositValue,
      note: broker.minDepositDescription,
    });
  }
  if (!isEmpty(broker.exchangeFeeValue)) {
    metrics.push({
      label: t.metrics.fxFee,
      value: broker.exchangeFeeValue,
      note: broker.exchangeFeeDescription,
    });
  }
  if (broker.hasSavingsInterest && !isEmpty(broker.savingsInterestValue)) {
    metrics.push({
      label: t.metrics.savingsInterest,
      value: broker.savingsInterestValue,
      note: broker.savingsInterestDescription,
    });
  }

  // Match grid columns to actual content so the row never looks "padded out".
  // Max is 4 (fee/courtage + min deposit + FX + savings).
  const lgColsClass =
    metrics.length >= 4
      ? 'lg:grid-cols-4'
      : metrics.length === 3
      ? 'lg:grid-cols-3'
      : metrics.length === 2
      ? 'lg:grid-cols-2'
      : 'lg:grid-cols-1';

  return (
    <article className="rounded-xl border border-gray-200 bg-white p-5 shadow-[0_1px_3px_rgb(15_23_42_/_0.1)] sm:p-6">
      {/* Identity row */}
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white p-1 text-base font-bold text-blue-900 sm:h-14 sm:w-14">
          {broker.logoUrl ? (
            <Image
              src={broker.logoUrl}
              alt={broker.title}
              width={56}
              height={56}
              className="h-full w-full rounded-lg object-contain"
              unoptimized
            />
          ) : (
            <span>{broker.title.slice(0, 2)}</span>
          )}
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-extrabold tracking-tight text-gray-900 sm:text-[19px]">
              {broker.title}
            </h3>
            {broker.focusIds[0] && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-[2px] text-[10px] font-bold uppercase tracking-wide text-blue-800">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="6" />
                  <circle cx="12" cy="12" r="2" />
                </svg>
                {t.focusOptions[broker.focusIds[0] as keyof typeof t.focusOptions] ?? broker.focusIds[0]}
              </span>
            )}
            {broker.fiLicensed && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-[2px] text-[10px] font-bold uppercase tracking-wide text-green-800">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
                  <path d="M12 2 4 6v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V6l-8-4z" />
                </svg>
                {t.fiLicense}
              </span>
            )}
          </div>
          {broker.tagline && (
            <p className="mt-0.5 text-sm text-gray-500">{broker.tagline}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <InfoTooltip text={t.ratingTooltip} />
            <span className="inline-flex gap-px">
              {[1, 2, 3, 4, 5].map((i) => (
                <span key={i}>{STAR_SVG(i <= Math.round(broker.appRatingNumber))}</span>
              ))}
            </span>
            <span className="text-xs text-gray-500">
              <strong className="text-gray-900">{broker.appRatingNumber.toFixed(1)}</strong>/5
              {broker.yearCreated ? ` · ${t.since} ${broker.yearCreated}` : ''}
            </span>
          </div>
        </div>
      </div>

      {broker.reviewText && (
        <p className="mt-4 text-sm leading-relaxed text-gray-700">{broker.reviewText}</p>
      )}

      {metrics.length > 0 && (
        <div
          className={`mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 ${lgColsClass}`}
        >
          {metrics.map((m) => (
            <Metric key={m.label} label={m.label} value={m.value} note={m.note} />
          ))}
        </div>
      )}

      {/* Strengths + weaknesses + offering */}
      <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-3">
        {broker.keyFeatures.length > 0 && (
          <div>
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
              {t.keyStrengths}
            </div>
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
              {broker.keyFeatures.map((f, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-700">
                  <svg className="mt-0.5 shrink-0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {broker.keyDownsides.length > 0 && (
          <div>
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
              {t.keyWeaknesses}
            </div>
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
              {broker.keyDownsides.map((d, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-700">
                  <svg className="mt-0.5 shrink-0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="3" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
            {t.availableAt.replace('{broker}', broker.title)}
          </div>
          <div className="flex flex-wrap gap-1">
            {[...broker.accountTypes, ...broker.productTypes].map((tag) => (
              <span
                key={tag}
                className="rounded-full px-2 py-[3px] text-[11px] font-semibold capitalize"
                style={{ background: `${ACCENT}12`, color: ACCENT }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {(broker.securityText || broker.taxText) && (
        <div className="mt-4 flex flex-col gap-1.5">
          {broker.securityText && (
            <ComplianceLine
              label={t.securityLabel}
              text={broker.securityText}
              icon={
                <path d="M12 2 4 6v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V6l-8-4z" />
              }
              stroke="#16a34a"
            />
          )}
          {broker.taxText && (
            <ComplianceLine
              label={t.taxLabel}
              text={broker.taxText}
              icon={
                <>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="8" y1="13" x2="16" y2="13" />
                  <line x1="8" y1="17" x2="13" y2="17" />
                </>
              }
              stroke="#1d4ed8"
            />
          )}
        </div>
      )}

      {broker.offerText && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5 text-xs leading-relaxed text-orange-900">
          <svg
            className="mt-px shrink-0"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#c2410c"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
          </svg>
          <span>
            <strong className="font-semibold">{t.offerLabel}:</strong> {broker.offerText}
          </span>
        </div>
      )}

      {/* CTA row */}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
        {broker.affiliateCtaUrl && (
          <a
            href={broker.affiliateCtaUrl}
            target="_blank"
            rel="noopener sponsored"
            className={
              broker.offerText
                ? 'group inline-flex items-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white no-underline shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:bg-orange-600 hover:text-white hover:shadow-md active:translate-y-0'
                : 'group inline-flex items-center gap-1.5 rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white no-underline shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:bg-blue-800 hover:text-white hover:shadow-md active:translate-y-0'
            }
          >
            {broker.offerText
              ? t.claimOffer.replace('{broker}', broker.title)
              : broker.affiliateCtaLabel || t.openAccount.replace('{broker}', broker.title)}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
              className="transition-transform duration-150 group-hover:translate-x-0.5"
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        )}
        {reviewCta && (
          <a
            href={reviewCta.url}
            className="group inline-flex items-center gap-1 px-3 py-2.5 text-sm font-semibold text-blue-700 no-underline transition-colors hover:text-blue-900"
          >
            <span className="group-hover:underline">{reviewCta.label}</span>
            <span aria-hidden="true" className="inline-block transition-transform duration-150 group-hover:translate-x-0.5">→</span>
          </a>
        )}
      </div>
    </article>
  );
}

function ComplianceLine({
  label,
  text,
  icon,
  stroke,
}: {
  label?: string;
  text: string;
  icon: React.ReactNode;
  stroke: string;
}) {
  return (
    <div className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-600">
      <svg
        className="mt-px shrink-0"
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke={stroke}
        strokeWidth="2"
        aria-hidden="true"
      >
        {icon}
      </svg>
      <span>
        {label && <strong className="font-semibold text-gray-800">{label}: </strong>}
        {text}
      </span>
    </div>
  );
}

function InfoTooltip({ text }: { text: string }) {
  // CSS-only tooltip: visible on hover or keyboard focus. Uses `peer-*` so the
  // bubble can sit on a sibling without trapping the cursor.
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        tabIndex={0}
        aria-label={text}
        className="peer flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-gray-300 text-[10px] font-bold text-gray-500 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
      >
        i
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-0 top-full z-10 mt-1.5 w-56 rounded-md bg-gray-900 px-2.5 py-1.5 text-[11px] font-medium leading-snug text-white opacity-0 shadow-lg transition-opacity peer-hover:opacity-100 peer-focus:opacity-100"
      >
        {text}
      </span>
    </span>
  );
}

function Metric({ label, value, note }: { label: string; value: string; note?: string }) {
  // Build a native tooltip combining label + value + (clipped) note so hovering
  // any part of the box reveals the full context — the note is visually
  // truncated to two lines.
  const tooltip = note ? `${label}: ${value}\n\n${note}` : `${label}: ${value}`;
  return (
    <div
      className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5"
      title={tooltip}
    >
      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className="text-[15px] font-bold text-gray-900">{value}</div>
      {note && (
        <div
          className="mt-0.5 text-[11px] leading-snug text-gray-600 line-clamp-2"
          title={note}
        >
          {note}
        </div>
      )}
    </div>
  );
}
